import { MedicalPaOrdersAuthStatus } from "../enums/medicalPaOrdersAuthStatus";

/**
 * Auth statuses that still require filing info when reached from an
 * `auth_required` order: in-flight RISA processing states and denial
 * outcomes. Extend this list if additional statuses should be treated the
 * same way.
 */
const FILING_REQUIRED_AFTER_AUTH_REQUIRED: MedicalPaOrdersAuthStatus[] = [
  MedicalPaOrdersAuthStatus.Pending,
  MedicalPaOrdersAuthStatus.AuthByRISA,
  MedicalPaOrdersAuthStatus.DeniedByRISA,
  MedicalPaOrdersAuthStatus.DenialByRISA,
  MedicalPaOrdersAuthStatus.DenialAfterQuery,
  MedicalPaOrdersAuthStatus.ExistingDenial,
];

/**
 * Computes whether filing info is required for a J-code/order.
 *
 * Always returns true when `newAuthStatus` is `no_auth_required` so the
 * modal can capture a not-applicable filing choice. Otherwise filing is
 * required only when `oldAuthStatus` was `auth_required` and
 * `newAuthStatus` is an in-flight RISA processing, denial, or auth-on-file
 * state (see {@link FILING_REQUIRED_AFTER_AUTH_REQUIRED}).
 *
 * @param oldAuthStatus - Auth status before the update.
 * @param newAuthStatus - Auth status after the update.
 * @returns True when the Filing Info cell or modal Save should be required.
 */
export const getFilingInfoRequired = (
  oldAuthStatus: string | undefined,
  newAuthStatus: string | undefined,
): boolean => {
  if (newAuthStatus === MedicalPaOrdersAuthStatus.NoAuthRequired) {
    return true;
  }
  if (oldAuthStatus !== MedicalPaOrdersAuthStatus.AuthRequired) {
    return false;
  }
  return FILING_REQUIRED_AFTER_AUTH_REQUIRED.includes(
    newAuthStatus as MedicalPaOrdersAuthStatus,
  );
};

/**
 * Determines whether the Filing Info badge is read-only (static).
 *
 * The badge stays interactive (`false`) only when filing info may need to be
 * entered: `no_auth_required` rows (modal opens for not-applicable filing) and
 * transitions from `auth_required` into a status listed in
 * {@link FILING_REQUIRED_AFTER_AUTH_REQUIRED}. All other combinations render a
 * non-clickable badge.
 *
 * @param oldAuthStatus - PA-required status before the update.
 * @param newAuthStatus - PA-required status after the update.
 * @returns True when the Filing Info cell should not open the modal.
 */
export const shouldTheFilingInfoBeStatic = (
  oldAuthStatus: string | undefined,
  newAuthStatus: string | undefined,
): boolean => {
  if (newAuthStatus === MedicalPaOrdersAuthStatus.NoAuthRequired) {
    return false;
  }
  if (oldAuthStatus !== MedicalPaOrdersAuthStatus.AuthRequired) {
    return true;
  }
  return !FILING_REQUIRED_AFTER_AUTH_REQUIRED.includes(
    newAuthStatus as MedicalPaOrdersAuthStatus,
  );
};
